import { z } from 'zod'

export const uuidSchema = z.uuid()

export const localeSchema = z.enum(['en', 'ar'])
export type Locale = z.infer<typeof localeSchema>

/**
 * Money and quantities travel as decimal strings, never as JS numbers, so that no
 * binary floating point ever touches an amount. OMR has 3 decimal places (baisa).
 */
export const MONEY_SCALE = 3
export const QUANTITY_SCALE = 4
/** An amount with up to 3 decimals, as a string. An empty one is missing rather than malformed. */
export const moneySchema = z.string().regex(/^-?\d{1,15}(\.\d{1,3})?$/, {
  error: (issue) => (issue.input === '' ? 'Required' : 'Invalid amount'),
})
export const quantitySchema = z.string().regex(/^-?\d{1,14}(\.\d{1,4})?$/, 'Invalid quantity')

/** Optional trimmed text: empty strings become null so forms can clear a field. */
export const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null))

export const sortSchema = z
  .string()
  .regex(/^-?[a-zA-Z][a-zA-Z0-9]*$/, 'Invalid sort')
  .optional()

export const pageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  /** Field name, prefixed with "-" for descending. */
  sort: sortSchema,
  q: z.string().trim().max(200).optional(),
})
export type PageQuery = z.infer<typeof pageQuerySchema>

export interface Page<T> {
  items: T[]
  page: number
  pageSize: number
  total: number
}

/** Optimistic concurrency: every update of a versioned aggregate must carry the version it read. */
export const versionSchema = z.number().int().min(1)

export const ErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  BUSINESS_RULE: 'BUSINESS_RULE',
  RATE_LIMITED: 'RATE_LIMITED',
  /** A database integrity rule refused the change (the application checks normally catch it first). */
  DATA_RULE: 'DATA_RULE',
  /** The record is still referenced by other records. */
  IN_USE: 'IN_USE',
  /** A concurrent change collided with this one; retrying is safe. */
  RETRY: 'RETRY',
  /** The user holds the permission, but the company's plan does not include the module (403). */
  MODULE_DISABLED: 'MODULE_DISABLED',
  /**
   * The user holds the permission, but only over part of the records (a data scope, ADR-025), and this
   * reader does not filter by scope: it answers only to users who reach every record (403).
   */
  SCOPE_RESTRICTED: 'SCOPE_RESTRICTED',
  /**
   * 403: the super-admin console's password sign-in is off on this server (SUPERADMIN_LOCAL_LOGIN=false);
   * super admins sign in through cloudTech Hub (ADR-042).
   */
  LOCAL_LOGIN_DISABLED: 'LOCAL_LOGIN_DISABLED',
  INTERNAL: 'INTERNAL',

  // ── platform services (R0b) ──
  /** 422: only a FAILED message can be retried. */
  MESSAGE_NOT_RETRYABLE: 'MESSAGE_NOT_RETRYABLE',
  /** 422: a template override uses a variable the template does not declare. */
  TEMPLATE_UNKNOWN_VARIABLE: 'TEMPLATE_UNKNOWN_VARIABLE',
  /** 422: a draft or void invoice cannot be e-mailed. */
  INVOICE_NOT_SENDABLE: 'INVOICE_NOT_SENDABLE',
  /** 422: the mail service is not allowed for the tenant's residency zone. */
  ADAPTER_NOT_ALLOWED_IN_ZONE: 'ADAPTER_NOT_ALLOWED_IN_ZONE',
  /** 409: the PDF is still being produced (with Retry-After). */
  DOCUMENT_RENDERING: 'DOCUMENT_RENDERING',
  /** 422: the PDF could not be produced. */
  DOCUMENT_FAILED: 'DOCUMENT_FAILED',
  /** 422: a draft or void document cannot be issued as a PDF. */
  DOCUMENT_NOT_ISSUABLE: 'DOCUMENT_NOT_ISSUABLE',
  /** 422: the customer was anonymised and can no longer be edited or restored. */
  CUSTOMER_ANONYMISED: 'CUSTOMER_ANONYMISED',
  /** 422: a retention period below the data class's legal floor. */
  RETENTION_BELOW_FLOOR: 'RETENTION_BELOW_FLOOR',
  /** 422: the subject is under a legal hold (or the hold was already lifted). */
  LEGAL_HOLD: 'LEGAL_HOLD',
  /** 422: the request cannot move to that status from where it is. */
  DSR_ILLEGAL_TRANSITION: 'DSR_ILLEGAL_TRANSITION',
  /** 422: an erasure of a subject still referenced by issued business documents. */
  DSR_SUBJECT_REFERENCED: 'DSR_SUBJECT_REFERENCED',
  /** 409: uploads are paused while the company's files move to another zone. */
  UPLOADS_PAUSED: 'UPLOADS_PAUSED',
  /** 409: a residency move is already in progress. */
  RESIDENCY_MOVE_ACTIVE: 'RESIDENCY_MOVE_ACTIVE',
  /** 422: the zone is not available on this deployment. */
  ZONE_NOT_CONFIGURED: 'ZONE_NOT_CONFIGURED',
  /** 404/422: the invitation link is unknown, used, revoked or expired (never says which). */
  INVITATION_INVALID: 'INVITATION_INVALID',
  /** 422: e-mail delivery through the company's own server needs a host, a user name and a sender address. */
  SMTP_INCOMPLETE: 'SMTP_INCOMPLETE',
  /** 422: the company's mail server resolves to a private, loopback or link-local address. */
  SMTP_HOST_NOT_ALLOWED: 'SMTP_HOST_NOT_ALLOWED',
} as const
export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode]

/** The R0b platform-service codes, which every screen can put in words (web `common:errors.codes`). */
export const PLATFORM_SERVICE_ERROR_CODES = [
  ErrorCode.MESSAGE_NOT_RETRYABLE,
  ErrorCode.TEMPLATE_UNKNOWN_VARIABLE,
  ErrorCode.INVOICE_NOT_SENDABLE,
  ErrorCode.ADAPTER_NOT_ALLOWED_IN_ZONE,
  ErrorCode.DOCUMENT_RENDERING,
  ErrorCode.DOCUMENT_FAILED,
  ErrorCode.DOCUMENT_NOT_ISSUABLE,
  ErrorCode.CUSTOMER_ANONYMISED,
  ErrorCode.RETENTION_BELOW_FLOOR,
  ErrorCode.LEGAL_HOLD,
  ErrorCode.DSR_ILLEGAL_TRANSITION,
  ErrorCode.DSR_SUBJECT_REFERENCED,
  ErrorCode.UPLOADS_PAUSED,
  ErrorCode.RESIDENCY_MOVE_ACTIVE,
  ErrorCode.ZONE_NOT_CONFIGURED,
  ErrorCode.INVITATION_INVALID,
  ErrorCode.SMTP_INCOMPLETE,
  ErrorCode.SMTP_HOST_NOT_ALLOWED,
] as const
export type PlatformServiceErrorCode = (typeof PLATFORM_SERVICE_ERROR_CODES)[number]

export function isPlatformServiceErrorCode(code: string): code is PlatformServiceErrorCode {
  return (PLATFORM_SERVICE_ERROR_CODES as readonly string[]).includes(code)
}

/** RFC 9457 problem details, extended with a stable machine-readable code. */
export interface ProblemDetails {
  type: string
  title: string
  status: number
  code: ErrorCode | (string & {})
  detail?: string
  errors?: { path: string; message: string }[]
  requestId?: string
}
